import React, { useState, useRef, useEffect } from 'react';
import { 
  Camera, 
  CameraOff, 
  Mic, 
  MicOff, 
  Monitor, 
  MonitorOff,
  Users,
  Video,
  VideoOff,
  Maximize,
  Minimize,
  Volume2,
  VolumeX,
  Settings,
  Grid,
  Square,
  Crown,
  Shield,
  MoreVertical
} from 'lucide-react';
import { webrtcService } from '../services/webrtcService';
import { useAuth } from '../context/AuthContext';

function MultiCameraView({ streamId, hosts, userType = 'viewer', onHostMediaChange }) {
  const { user } = useAuth();
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [mediaStates, setMediaStates] = useState(new Map());
  const [layout, setLayout] = useState('grid'); // 'grid', 'spotlight', 'sidebar'
  const [spotlightHost, setSpotlightHost] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [audioLevels, setAudioLevels] = useState(new Map());
  const [showControls, setShowControls] = useState(true);
  
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef(new Map());
  const audioContextRef = useRef(null);
  const analyzersRef = useRef(new Map());

  useEffect(() => {
    if (streamId) {
      initializeMultiCamera();
    }

    return () => {
      cleanup();
    };
  }, [streamId]);

  useEffect(() => {
    // Setup audio level monitoring for all hosts
    setupAudioLevelMonitoring();
  }, [remoteStreams, localStream]);

  const initializeMultiCamera = async () => {
    try {
      // Set up WebRTC event handlers
      webrtcService.onRemoteStream = handleRemoteStream;
      webrtcService.onHostJoined = handleHostJoined;
      webrtcService.onHostLeft = handleHostLeft;
      webrtcService.onHostMediaStateChanged = handleHostMediaStateChanged;
      
      // Join the stream
      await webrtcService.joinStream(streamId, userType);
      setIsConnected(true);
      
      // If user is a host, start their camera
      if (userType === 'host' || hosts.some(h => h.id === user?.id)) {
        await startLocalCamera();
      }
    } catch (error) {
      console.error('Failed to initialize multi-camera:', error);
    }
  };

  const startLocalCamera = async () => {
    try {
      const stream = await webrtcService.startCamera({ video: true, audio: true });
      setLocalStream(stream);
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      // Update media state
      setMediaStates(prev => new Map(prev.set(user?.id, {
        camera: true,
        microphone: true,
        screenShare: false,
      })));
      
      onHostMediaChange?.(user?.id, { camera: true, microphone: true, screenShare: false });
    } catch (error) {
      console.error('Failed to start local camera:', error);
    }
  };

  const handleRemoteStream = (hostId, stream) => {
    console.log('Received remote stream from host:', hostId);
    setRemoteStreams(prev => new Map(prev.set(hostId, stream)));
    
    // Set up video element for this stream
    const videoElement = remoteVideoRefs.current.get(hostId);
    if (videoElement) {
      videoElement.srcObject = stream;
    }
  };

  const handleHostJoined = (hostInfo) => {
    console.log('Host joined:', hostInfo);
    // Initialize media state for new host
    setMediaStates(prev => new Map(prev.set(hostInfo.userId, {
      camera: false,
      microphone: false,
      screenShare: false,
    })));
  };

  const handleHostLeft = (hostId) => {
    console.log('Host left:', hostId);
    setRemoteStreams(prev => {
      const newMap = new Map(prev);
      newMap.delete(hostId);
      return newMap;
    });
    setMediaStates(prev => {
      const newMap = new Map(prev);
      newMap.delete(hostId);
      return newMap;
    });
    setAudioLevels(prev => {
      const newMap = new Map(prev);
      newMap.delete(hostId);
      return newMap;
    });
  };

  const handleHostMediaStateChanged = (hostId, mediaState) => {
    setMediaStates(prev => new Map(prev.set(hostId, mediaState)));
    onHostMediaChange?.(hostId, mediaState);
  };

  const setupAudioLevelMonitoring = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Monitor local stream
      if (localStream) {
        setupAudioAnalyzer(user?.id, localStream);
      }

      // Monitor remote streams
      remoteStreams.forEach((stream, hostId) => {
        setupAudioAnalyzer(hostId, stream);
      });
    } catch (error) {
      console.error('Failed to setup audio monitoring:', error);
    }
  };

  const setupAudioAnalyzer = (hostId, stream) => {
    try {
      const audioContext = audioContextRef.current;
      const audioTracks = stream.getAudioTracks();
      
      if (audioTracks.length === 0) return;

      const source = audioContext.createMediaStreamSource(stream);
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 256;
      
      source.connect(analyzer);
      analyzersRef.current.set(hostId, analyzer);

      // Start monitoring audio levels
      const monitorLevel = () => {
        const dataArray = new Uint8Array(analyzer.frequencyBinCount);
        analyzer.getByteFrequencyData(dataArray);
        
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        const normalizedLevel = Math.min(average / 128, 1);
        
        setAudioLevels(prev => new Map(prev.set(hostId, normalizedLevel)));
        
        if (analyzersRef.current.has(hostId)) {
          requestAnimationFrame(monitorLevel);
        }
      };
      
      monitorLevel();
    } catch (error) {
      console.error('Failed to setup audio analyzer for host:', hostId, error);
    }
  };

  const toggleLocalCamera = async () => {
    try {
      const currentState = mediaStates.get(user?.id);
      if (currentState?.camera) {
        // Turn off camera
        if (localStream) {
          localStream.getVideoTracks().forEach(track => track.stop());
        }
        setMediaStates(prev => new Map(prev.set(user?.id, { ...currentState, camera: false })));
      } else {
        // Turn on camera
        await startLocalCamera();
      }
    } catch (error) {
      console.error('Failed to toggle camera:', error);
    }
  };

  const toggleLocalMicrophone = () => {
    const currentState = mediaStates.get(user?.id);
    if (localStream) {
      const audioTracks = localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !currentState?.microphone;
      });
      setMediaStates(prev => new Map(prev.set(user?.id, { 
        ...currentState, 
        microphone: !currentState?.microphone 
      })));
    }
  };

  const toggleScreenShare = async () => {
    try {
      const currentState = mediaStates.get(user?.id);
      if (currentState?.screenShare) {
        await webrtcService.stopScreenShare();
        setMediaStates(prev => new Map(prev.set(user?.id, { ...currentState, screenShare: false })));
      } else {
        await webrtcService.shareScreen();
        setMediaStates(prev => new Map(prev.set(user?.id, { ...currentState, screenShare: true })));
      }
    } catch (error) {
      console.error('Failed to toggle screen share:', error);
    }
  };

  const setSpotlight = (hostId) => {
    setSpotlightHost(hostId);
    setLayout('spotlight');
  };

  const getHostInfo = (hostId) => {
    return hosts.find(h => h.id === hostId) || {
      id: hostId,
      displayName: `Host ${hostId.slice(-4)}`,
      role: 'co-host',
      avatar: null,
    };
  };

  const getLayoutClasses = () => {
    const totalHosts = hosts.filter(h => h.isOnline).length;
    
    switch (layout) {
      case 'spotlight':
        return 'grid-spotlight';
      case 'sidebar':
        return 'grid-sidebar';
      default: // grid
        if (totalHosts === 1) return 'grid-cols-1';
        if (totalHosts === 2) return 'grid-cols-1 md:grid-cols-2';
        if (totalHosts <= 4) return 'grid-cols-2';
        if (totalHosts <= 6) return 'grid-cols-2 md:grid-cols-3';
        return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
    }
  };

  const renderVideoElement = (hostId, isLocal = false, isSpotlight = false) => {
    const hostInfo = getHostInfo(hostId);
    const mediaState = mediaStates.get(hostId);
    const audioLevel = audioLevels.get(hostId) || 0;
    const stream = isLocal ? localStream : remoteStreams.get(hostId);

    const videoClasses = `
      relative overflow-hidden rounded-lg bg-gray-900
      ${isSpotlight ? 'aspect-video' : 'aspect-video'}
      ${audioLevel > 0.1 ? 'ring-2 ring-green-400' : ''}
    `;

    return (
      <div key={hostId} className={videoClasses}>
        <video
          ref={(el) => {
            if (isLocal) {
              localVideoRef.current = el;
            } else {
              remoteVideoRefs.current.set(hostId, el);
            }
            if (el && stream) {
              el.srcObject = stream;
            }
          }}
          autoPlay
          muted={isLocal}
          playsInline
          className="w-full h-full object-cover"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
        
        {/* Host Info */}
        <div className="absolute top-3 left-3 flex items-center space-x-2">
          <img
            src={hostInfo.avatar || `https://via.placeholder.com/24x24/0051c3/ffffff?text=${hostInfo.displayName?.charAt(0) || 'H'}`}
            alt={hostInfo.displayName}
            className="w-6 h-6 rounded-full"
          />
          <span className="text-white text-sm font-medium">{hostInfo.displayName}</span>
          {hostInfo.role === 'primary-host' && <Crown className="w-4 h-4 text-yellow-400" />}
          {hostInfo.role === 'moderator' && <Shield className="w-4 h-4 text-purple-400" />}
        </div>
        
        {/* Media Controls (for current user) */}
        {isLocal && showControls && (
          <div className="absolute top-3 right-3 flex items-center space-x-1">
            <button
              onClick={toggleLocalCamera}
              className={`p-1 rounded ${mediaState?.camera ? 'bg-green-500' : 'bg-red-500'} text-white`}
            >
              {mediaState?.camera ? <Camera className="w-4 h-4" /> : <CameraOff className="w-4 h-4" />}
            </button>
            <button
              onClick={toggleLocalMicrophone}
              className={`p-1 rounded ${mediaState?.microphone ? 'bg-green-500' : 'bg-red-500'} text-white`}
            >
              {mediaState?.microphone ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
            </button>
          </div>
        )}
        
        {/* Bottom Bar */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* Media Status */}
            <div className="flex items-center space-x-1">
              {mediaState?.camera ? (
                <Video className="w-4 h-4 text-green-400" />
              ) : (
                <VideoOff className="w-4 h-4 text-red-400" />
              )}
              {mediaState?.microphone ? (
                <Volume2 className="w-4 h-4 text-green-400" />
              ) : (
                <VolumeX className="w-4 h-4 text-red-400" />
              )}
            </div>
            
            {/* Audio Level Indicator */}
            {audioLevel > 0.05 && (
              <div className="w-2 h-4 bg-gray-700 rounded overflow-hidden">
                <div 
                  className="w-full bg-green-400 transition-all duration-100"
                  style={{ height: `${audioLevel * 100}%`, transform: 'translateY(100%)' }}
                />
              </div>
            )}
          </div>
          
          {/* Actions */}
          {!isSpotlight && (
            <button
              onClick={() => setSpotlight(hostId)}
              className="p-1 bg-black bg-opacity-50 text-white rounded hover:bg-opacity-70"
            >
              <Maximize className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* No Camera State */}
        {!mediaState?.camera && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
            <div className="text-center">
              <CameraOff className="w-8 h-8 text-gray-500 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">{hostInfo.displayName}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const cleanup = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    remoteStreams.forEach(stream => {
      stream.getTracks().forEach(track => track.stop());
    });
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    webrtcService.leaveStream();
  };

  if (!isConnected) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-cf-orange border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Connecting to multi-camera stream...</p>
        </div>
      </div>
    );
  }

  const onlineHosts = hosts.filter(h => h.isOnline);
  const currentUserIsHost = hosts.some(h => h.id === user?.id);

  return (
    <div className="space-y-4">
      {/* Layout Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-gray-400 text-sm">Layout:</span>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setLayout('grid')}
              className={`p-2 rounded ${layout === 'grid' ? 'bg-cf-orange text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLayout('spotlight')}
              className={`p-2 rounded ${layout === 'spotlight' ? 'bg-cf-orange text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              <Square className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-gray-400 text-sm">
            {onlineHosts.length} host{onlineHosts.length !== 1 ? 's' : ''} online
          </span>
          
          {currentUserIsHost && (
            <button
              onClick={toggleScreenShare}
              className={`p-2 rounded ${mediaStates.get(user?.id)?.screenShare ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              {mediaStates.get(user?.id)?.screenShare ? <MonitorOff className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Video Grid */}
      {layout === 'spotlight' && spotlightHost ? (
        <div className="space-y-4">
          {/* Spotlight Video */}
          <div className="w-full">
            {renderVideoElement(spotlightHost, spotlightHost === user?.id, true)}
          </div>
          
          {/* Other Hosts Sidebar */}
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {onlineHosts
              .filter(h => h.id !== spotlightHost)
              .map(host => renderVideoElement(host.id, host.id === user?.id))
            }
          </div>
          
          <button
            onClick={() => setLayout('grid')}
            className="btn-secondary flex items-center space-x-2 mx-auto"
          >
            <Minimize className="w-4 h-4" />
            <span>Exit Spotlight</span>
          </button>
        </div>
      ) : (
        /* Grid Layout */
        <div className={`grid gap-4 ${getLayoutClasses()}`}>
          {onlineHosts.map(host => 
            renderVideoElement(host.id, host.id === user?.id)
          )}
        </div>
      )}

      {/* No Hosts Message */}
      {onlineHosts.length === 0 && (
        <div className="text-center py-12 bg-gray-800 rounded-lg">
          <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No Hosts Online</h3>
          <p className="text-gray-500">Waiting for hosts to join the stream...</p>
        </div>
      )}
    </div>
  );
}

export default MultiCameraView; 